import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance, { clearAuthData, isJwtAuthFailure } from "../../components/content/axiosConfig";
import { enqueueSnackbar } from "notistack";
import Swal from "sweetalert2";
import Preloader from "../../components/custom/preLoader";
import ListPagination from "../../components/custom/ListPagination";
import useServerPaginatedList from "../../hooks/useServerPaginatedList";
import { openAdminPanelAsUser } from "../../utils/adminHandoff";
import showErrorToast, { showSuccessToast } from "../../components/toast/toast.js";
import { formatDisplayDateTime as formatDateTime } from "../../utils/formatDisplayDate";

function escapeHtml(value) {
    return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

const Scientists = () => {
    const [loggingInId, setLoggingInId] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [viewingId, setViewingId] = useState(null);

    const fetchScientistsPage = useCallback(async ({ search, page, limit }) => {
        try {
            return await axiosInstance.get("/api/User/GetAllScientists", {
                params: { search, page, limit },
            });
        } catch (error) {
            if (isJwtAuthFailure(error)) {
                clearAuthData();
                window.location.href = "/user/login";
                return { data: { data: [], total: 0 } };
            }
            const errorMessage = error.response?.data?.message || "An error occurred.";
            enqueueSnackbar(errorMessage, {
                variant: "error",
                anchorOrigin: { vertical: "top", horizontal: "right" },
                autoHideDuration: 3000,
            });
            throw error;
        }
    }, []);

    const {
        rows: scientists,
        totalCount,
        searchTerm,
        setSearchTerm,
        currentPage,
        setCurrentPage,
        isLoading,
        entriesPerPage,
        refetch,
    } = useServerPaginatedList(fetchScientistsPage);

    const loginToAdminPanel = async (staffId) => {
        if (loggingInId != null) return;
        setLoggingInId(staffId);
        try {
            const res = await axiosInstance.post("/api/SuperUser/LoginAsAdmin", { staffId });
            openAdminPanelAsUser(res.data);
            showSuccessToast("Opening Admin panel…");
        } catch (error) {
            if (isJwtAuthFailure(error)) {
                clearAuthData();
                window.location.href = "/user/login";
                return;
            }
            showErrorToast(error.response?.data?.message || "Could not open Admin panel");
        } finally {
            setLoggingInId(null);
        }
    };

    const viewCollaborations = async (scientist) => {
        if (viewingId != null) return;
        setViewingId(scientist.id);
        try {
            const res = await axiosInstance.get(`/api/User/GetScientistCollaborations/${scientist.id}`);
            const accounts = Array.isArray(res.data) ? res.data : [];
            const listHtml = accounts.length === 0
                ? '<p class="text-muted mb-0">This scientist is not linked to any accounts yet.</p>'
                : `<div class="text-start">${accounts.map((account) => `
                    <div class="border rounded p-2 mb-2">
                      <div class="fw-semibold">${escapeHtml(account.FullName)}</div>
                      <div class="small text-muted">${escapeHtml(account.Email)}</div>
                      <div class="small mt-1">
                        <span class="badge ${account.Status ? "bg-success" : "bg-secondary"} me-1">
                          ${account.Status ? "Active" : "Inactive"}
                        </span>
                        <a href="#" class="small scientist-account-link" data-href="/staff/${account.fisherId}">View account staff</a>
                      </div>
                    </div>
                  `).join("")}</div>`;

            await Swal.fire({
                title: `Accounts — ${scientist.FullName}`,
                html: listHtml,
                width: 520,
                confirmButtonText: "Close",
                confirmButtonColor: "#1b5e20",
                didOpen: () => {
                    const container = Swal.getHtmlContainer();
                    if (!container) return;
                    container.querySelectorAll(".scientist-account-link").forEach((link) => {
                        link.addEventListener("click", (event) => {
                            event.preventDefault();
                            const href = link.getAttribute("data-href");
                            if (href) window.location.href = href;
                        });
                    });
                },
            });
        } catch (error) {
            if (isJwtAuthFailure(error)) {
                clearAuthData();
                window.location.href = "/user/login";
                return;
            }
            showErrorToast(error.response?.data?.message || "Could not load linked accounts");
        } finally {
            setViewingId(null);
        }
    };

    const deleteScientist = async (scientist) => {
        if (deletingId != null) return;

        const result = await Swal.fire({
            title: "Delete this scientist?",
            text: "This permanently removes the scientist and all organization links.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Yes, delete",
            cancelButtonText: "Cancel",
            focusCancel: true,
        });

        if (!result.isConfirmed) return;

        setDeletingId(scientist.id);
        try {
            await axiosInstance.delete("/api/User/DeleteIndependentScientist", {
                params: { id: scientist.id },
            });
            showSuccessToast("Scientist deleted successfully");
            await refetch();
        } catch (error) {
            if (isJwtAuthFailure(error)) {
                clearAuthData();
                window.location.href = "/user/login";
                return;
            }
            showErrorToast(error.response?.data?.message || "Failed to delete scientist");
        } finally {
            setDeletingId(null);
        }
    };

    if (isLoading && scientists.length === 0 && deletingId == null) {
        return <Preloader />;
    }

    return (
        <div>
            <div className="d-flex align-items-center mb-3">
                <div>
                    <ul className="breadcrumb">
                        <li className="breadcrumb-item">
                            <Link to="/dashboard">Home</Link>
                        </li>
                        <li className="breadcrumb-item active">Scientists</li>
                    </ul>
                    <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                        <h1 className="page-header mb-0">Scientists</h1>
                        <span className="badge rounded-pill bg-light text-dark border px-2 py-1">
                            {isLoading ? "…" : totalCount}
                        </span>
                    </div>
                    <p className="text-muted mb-0 small">
                        Independent scientists registered on the platform
                    </p>
                </div>
                <div className="ms-auto" />
            </div>

            <div className="card border-0 shadow-sm">
                <div className="tab-content p-3">
                    <div className="input-group mb-3">
                        <div className="flex-fill position-relative">
                            <div className="input-group">
                                <div
                                    className="input-group-text position-absolute top-0 bottom-0 bg-none border-0 start-0"
                                    style={{ zIndex: 10 }}
                                >
                                    <i className="fa fa-search opacity-5" />
                                </div>
                                <input
                                    type="text"
                                    className="form-control px-35px bg-light"
                                    placeholder="Search by name or email..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="table-responsive mb-3 position-relative">
                        {deletingId != null ? (
                            <div
                                className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75"
                                style={{ zIndex: 5 }}
                            >
                                <div className="text-muted">
                                    <i className="fa fa-spinner fa-spin me-2" />
                                    Deleting scientist…
                                </div>
                            </div>
                        ) : null}
                        <table className="table table-hover table-panel text-nowrap align-middle mb-0">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Collaborations</th>
                                    <th>Status</th>
                                    <th>Registered</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr>
                                        <td colSpan={6} className="text-center text-muted py-5">
                                            Loading…
                                        </td>
                                    </tr>
                                ) : scientists.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="text-center text-muted py-5">
                                            No scientists found
                                        </td>
                                    </tr>
                                ) : (
                                    scientists.map((scientist) => (
                                        <tr key={scientist.id}>
                                            <td>{scientist.FullName}</td>
                                            <td>{scientist.Email}</td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="btn btn-link p-0 border-0 text-decoration-none"
                                                    title="View linked accounts"
                                                    disabled={viewingId != null}
                                                    onClick={() => viewCollaborations(scientist)}
                                                >
                                                    {scientist.Collaborations ?? 0}
                                                    <i className="fa fa-external-link ms-1 fs-11px opacity-75" />
                                                </button>
                                            </td>
                                            <td>
                                                {scientist.Status ? (
                                                    <span className="badge border border-success text-success px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center">
                                                        <i className="fa fa-circle fs-9px fa-fw me-5px" />
                                                        Active
                                                    </span>
                                                ) : (
                                                    <span className="badge border border-danger text-warning px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center">
                                                        <i className="fa fa-circle fs-9px fa-fw me-5px" />
                                                        Inactive
                                                    </span>
                                                )}
                                            </td>
                                            <td>{formatDateTime(scientist.createdAt)}</td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="btn btn-link p-0 border-0 text-primary"
                                                    title="Open Admin panel as this scientist"
                                                    disabled={loggingInId != null || deletingId != null}
                                                    onClick={() => loginToAdminPanel(scientist.id)}
                                                >
                                                    <i className="fa fa-sign-in fs-13px fa-fw me-5px" />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-link p-0 border-0 text-primary"
                                                    title="View linked accounts"
                                                    disabled={viewingId != null || deletingId != null}
                                                    onClick={() => viewCollaborations(scientist)}
                                                >
                                                    <i className="fa fa-building fs-13px fa-fw me-5px" />
                                                </button>
                                                <Link
                                                    to={`/UpdateScientist/${scientist.id}`}
                                                    title="Edit scientist"
                                                >
                                                    <i className="fa fa-pencil fs-13px fa-fw me-5px" />
                                                </Link>
                                                <button
                                                    type="button"
                                                    className="btn btn-link p-0 border-0 text-danger"
                                                    title="Delete scientist"
                                                    disabled={deletingId != null || loggingInId != null}
                                                    onClick={() => deleteScientist(scientist)}
                                                >
                                                    <i className="fa fa-trash fs-13px fa-fw me-5px" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    <ListPagination
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        totalCount={totalCount}
                        entriesPerPage={entriesPerPage}
                    />
                </div>
            </div>
        </div>
    );
};

export default Scientists;
